import {
  DEFAULT_FILTER_MODEL_STATE,
  TransformedIssueSLAsDto,
  useDataGridContext,
  useIssueStatuses,
} from '@issuemanager/apps/client/components';
import { useUserContext } from '@issuemanager/apps/client/contexts';
import {
  ManagedIssuesEnum,
  QuickFilterAllEnum,
} from '@issuemanager/test/constants';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import {
  GetIssueResponseDtoStatusEnum,
  ManagedIssuesDto,
} from '@issuemanager/api-client';
import {
  IssueActionProgressEnum,
  ManagedIssuesTypeEnum,
  SLAStatusEnum,
} from '@issuemanager/constants';
import { useIssueActionProgress } from '../issue-action-progress-label';
import { QuickFilterItem } from './quick-filter-item';
import { QuickFilterContainer } from './quick-filter-container';

export function ManagedIssues({
  managedIssues,
  slaDueDays,
  activeFilter,
  setActiveFilter,
}: {
  managedIssues: ManagedIssuesDto | undefined;
  slaDueDays: Array<number> | undefined;
  activeFilter: QuickFilterAllEnum | null;
  setActiveFilter: (value: QuickFilterAllEnum) => void;
}) {
  const context = useDataGridContext();
  const user = useUserContext();
  const actionProgress = useIssueActionProgress();
  const { t } = useTranslation('issue');
  const issueStatuses = useIssueStatuses();

  const clearFilters = () => {
    context.setFilterModel(DEFAULT_FILTER_MODEL_STATE);
  };

  const actionProgressNotInitiated = useMemo(
    () =>
      actionProgress.find(
        (a) => a.value === IssueActionProgressEnum.NOT_INITIATED,
      ),
    [actionProgress],
  );
  const actionProgressInitiated = useMemo(
    () =>
      actionProgress.find((a) => a.value === IssueActionProgressEnum.INITIATED),
    [actionProgress],
  );
  const actionProgressOngoing = useMemo(
    () =>
      actionProgress.find((a) => a.value === IssueActionProgressEnum.ONGOING),
    [actionProgress],
  );
  const issueStatusInProgress = useMemo(
    () =>
      issueStatuses.find(
        (s) => s.value === GetIssueResponseDtoStatusEnum.InProgress,
      ),
    [issueStatuses],
  );
  const issueStatusOpen = useMemo(
    () =>
      issueStatuses.find((s) => s.value === GetIssueResponseDtoStatusEnum.Open),
    [issueStatuses],
  );
  const issueStatusToBeApproved = useMemo(
    () =>
      issueStatuses.find(
        (s) => s.value === GetIssueResponseDtoStatusEnum.ToBeApproved,
      ),
    [issueStatuses],
  );

  const slaDays: TransformedIssueSLAsDto[] =
    slaDueDays?.map((day) => ({
      id: String(day),
      slaDueDays: day,
    })) ?? [];

  const handleFilterSelection = (type: string) => {
    clearFilters();
    switch (type) {
      case ManagedIssuesTypeEnum.ALL:
        context.setColumnVisibilityModel((prevState) => ({
          ...prevState,
          manager: true,
        }));
        context.setFilterModel(
          (prevState) => ({
            ...prevState,
            manager: [user.data],
          }),
          'quick-filter',
        );
        break;
      case ManagedIssuesTypeEnum.ACTIONS_NOT_INITIATED:
        context.setColumnVisibilityModel((prevState) => ({
          ...prevState,
          manager: true,
          actionProgress: true,
        }));
        context.setFilterModel(
          (prevState) => ({
            ...prevState,
            manager: [user.data],
            actionProgress: [actionProgressNotInitiated],
          }),
          'quick-filter',
        );
        break;
      case ManagedIssuesTypeEnum.ACTIONS_INITIATED:
        context.setColumnVisibilityModel((prevState) => ({
          ...prevState,
          manager: true,
          actionProgress: true,
        }));
        context.setFilterModel(
          (prevState) => ({
            ...prevState,
            manager: [user.data],
            actionProgress: [actionProgressInitiated],
          }),
          'quick-filter',
        );
        break;
      case ManagedIssuesTypeEnum.ONGOING:
        context.setColumnVisibilityModel((prevState) => ({
          ...prevState,
          manager: true,
          actionProgress: true,
        }));
        context.setFilterModel(
          (prevState) => ({
            ...prevState,
            manager: [user.data],
            actionProgress: [actionProgressOngoing],
          }),
          'quick-filter',
        );
        break;
      case ManagedIssuesTypeEnum.OVERDUE:
        context.setColumnVisibilityModel((prevState) => ({
          ...prevState,
          manager: true,
          dueDate: true,
          status: true,
        }));
        context.setFilterModel(
          (prevState) => ({
            ...prevState,
            manager: [user.data],
            dueDate: [null, dayjs(new Date())],
            status: [
              issueStatusInProgress,
              issueStatusOpen,
              issueStatusToBeApproved,
            ],
          }),
          'quick-filter',
        );
        break;
      case ManagedIssuesTypeEnum.SLA_OVERDUE:
        context.setColumnVisibilityModel((prevState) => ({
          ...prevState,
          manager: true,
          slaDueDays: true,
        }));
        context.setFilterModel(
          (prevState) => ({
            ...prevState,
            manager: [user.data],
            slaDueDays: slaDays,
            slaStatus: [SLAStatusEnum.OVERDUE],
          }),
          'quick-filter',
        );
        break;
    }
  };

  return (
    <QuickFilterContainer label={t('quickFilters.managedIssues.title')}>
      <QuickFilterItem
        label={t('quickFilters.managedIssues.all')}
        number={managedIssues?.all}
        onClick={() => {
          handleFilterSelection(ManagedIssuesTypeEnum.ALL);
          setActiveFilter(ManagedIssuesEnum.ALL);
        }}
        isActive={activeFilter === ManagedIssuesEnum.ALL}
      />
      <QuickFilterItem
        label={t('quickFilters.managedIssues.actionsNotInitiated')}
        number={managedIssues?.notInitiated}
        onClick={() => {
          handleFilterSelection(ManagedIssuesTypeEnum.ACTIONS_NOT_INITIATED);
          setActiveFilter(ManagedIssuesEnum.ACTIONS_NOT_INITIATED);
        }}
        isActive={activeFilter === ManagedIssuesEnum.ACTIONS_NOT_INITIATED}
      />
      <QuickFilterItem
        label={t('quickFilters.managedIssues.actionsInitiated')}
        number={managedIssues?.initiated}
        onClick={() => {
          handleFilterSelection(ManagedIssuesTypeEnum.ACTIONS_INITIATED);
          setActiveFilter(ManagedIssuesEnum.ACTIONS_INITIATED);
        }}
        isActive={activeFilter === ManagedIssuesEnum.ACTIONS_INITIATED}
      />
      <QuickFilterItem
        label={t('quickFilters.managedIssues.ongoing')}
        number={managedIssues?.ongoing}
        onClick={() => {
          handleFilterSelection(ManagedIssuesTypeEnum.ONGOING);
          setActiveFilter(ManagedIssuesEnum.ONGOING);
        }}
        isActive={activeFilter === ManagedIssuesEnum.ONGOING}
      />
      <QuickFilterItem
        label={t('quickFilters.managedIssues.overdue')}
        number={managedIssues?.overdue}
        onClick={() => {
          handleFilterSelection(ManagedIssuesTypeEnum.OVERDUE);
          setActiveFilter(ManagedIssuesEnum.OVERDUE);
        }}
        isActive={activeFilter === ManagedIssuesEnum.OVERDUE}
      />
      <QuickFilterItem
        label={t('quickFilters.managedIssues.slaOverdue')}
        number={managedIssues?.slaOverdue}
        onClick={() => {
          handleFilterSelection(ManagedIssuesTypeEnum.SLA_OVERDUE);
          setActiveFilter(ManagedIssuesEnum.SLA_OVERDUE);
        }}
        isActive={activeFilter === ManagedIssuesEnum.SLA_OVERDUE}
      />
    </QuickFilterContainer>
  );
}
